// Shared live image-grade for the render turntable (QA tuning tool).
//
// The grading sliders live in the customize tab bar (EnamorInlineCustomize) but
// the SVG filter is applied on the turntable <img> (RenderTurntable) — two
// sibling components. This tiny module-level store lets the tab drive the
// turntable's filter without occluding the frame or threading props/redux.
import { useEffect, useState } from "react";

export const GRADE_DEFAULTS = {
  exposure: 100, brightness: 0, contrast: 100, warmth: 0, saturation: 100, sharpness: 0,
};

// label + neutral value + slider range for each control (order = display order).
export const GRADE_CONTROLS = [
  { key: "exposure", label: "Exposure", min: 20, max: 200, neutral: 100 },
  { key: "brightness", label: "Brightness", min: -100, max: 100, neutral: 0 },
  { key: "contrast", label: "Contrast", min: 0, max: 200, neutral: 100 },
  { key: "warmth", label: "Warmth", min: -100, max: 100, neutral: 0 },
  { key: "saturation", label: "Saturation", min: 0, max: 200, neutral: 100 },
  { key: "sharpness", label: "Sharpness", min: 0, max: 100, neutral: 0 },
];

// QA only — surfaced when REACT_APP_ENV=qa or the host URL carries ?tune=1.
export const gradeEnabled = () =>
  process.env.REACT_APP_ENV === "qa" ||
  (typeof window !== "undefined" && /[?&]tune=1/.test(window.location.search));

let state = { ...GRADE_DEFAULTS };
const listeners = new Set();

export const getGrade = () => state;
export const setGrade = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l(state));
};
export const resetGrade = () => setGrade(GRADE_DEFAULTS);
export const isGraded = () => GRADE_CONTROLS.some((c) => state[c.key] !== c.neutral);

// Subscribe a React component to grade changes.
export const useGrade = () => {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => listeners.delete(l);
  }, []);
  return state;
};

// Map the six slider values → SVG filter primitive params. exposure/warmth feed
// per-channel linear slopes, contrast pivots at mid-grey, brightness is additive,
// saturation is a colour-matrix, sharpness a convolve kernel.
export const gradeToFilter = (g) => {
  const S = g.saturation / 100;
  const C = g.contrast / 100;
  const E = g.exposure / 100;
  const B = (g.brightness / 100) * 0.25; // ±0.25 additive
  const w = g.warmth / 100;
  const wr = 1 + 0.25 * w;
  const wg = 1 + 0.06 * w;
  const wb = 1 - 0.25 * w;
  const intercept = 0.5 - 0.5 * C + B;
  const a = (g.sharpness / 100) * 1.2; // unsharp amount
  return {
    saturate: S.toFixed(3),
    slopeR: (C * E * wr).toFixed(3),
    slopeG: (C * E * wg).toFixed(3),
    slopeB: (C * E * wb).toFixed(3),
    intercept: intercept.toFixed(3),
    sharpen: a,
    kernel: `0 ${-a} 0 ${-a} ${1 + 4 * a} ${-a} 0 ${-a} 0`,
  };
};
